import { useState } from 'react';
import { api } from '../lib/api';
import type { Device } from '../lib/api';
import { ConfirmDialog, ErrorNotice, Notice, Panel, formatDateTime } from './ui';

/**
 * Who a device is assigned to, and the only place an administrator can change
 * it.
 *
 * Release and assign are separate actions rather than an editable name field,
 * and that is a deliberate design choice rather than a UI convenience. Editing
 * a name in place would produce one ambiguous audit line and would make
 * "correct a typo" indistinguishable from "this tablet went to a different
 * student". Releasing states that the device is now unattributed; assigning
 * states who it went to next. Both are recorded.
 *
 * The enrolling agent can create an assignment where there is none and can
 * never replace one, so this panel is the whole administrative surface for
 * reassignment — hence the confirmation dialog, which names the student and the
 * device rather than asking "are you sure?".
 */
export function AssignmentPanel({
  device,
  onChanged,
}: {
  device: Device;
  onChanged: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [newName, setNewName] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [message, setMessage] = useState<string | null>(null);

  const student = device.student;
  const deviceLabel =
    [device.manufacturer, device.model].filter(Boolean).join(' ') || device.displayName;

  const release = async () => {
    if (!student?.studentRef) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/devices/${device.id}/assignment/release`, {
        // Echoed back so a release posted from a stale page cannot remove
        // whoever happens to hold the device now.
        confirmStudentRef: student.studentRef,
        reason: reason.trim() || undefined,
      });
      setMessage(`Released ${student.studentName} (${student.studentRef}) from this device.`);
      setConfirming(false);
      setReason('');
      onChanged();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const assign = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<{ assignment: { studentRef: string; studentName: string } }>(
        `/devices/${device.id}/assignment`,
        { studentName: name, reason: reason.trim() || undefined },
      );
      setMessage(
        `Assigned to ${result.assignment.studentName} (${result.assignment.studentRef}).`,
      );
      setAssigning(false);
      setNewName('');
      setReason('');
      onChanged();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel
      title="Assignment"
      actions={
        student ? (
          <button type="button" className="danger-outline small" onClick={() => setConfirming(true)}>
            Release assignment
          </button>
        ) : (
          <button type="button" className="small" onClick={() => setAssigning(true)}>
            Assign a student
          </button>
        )
      }
    >
      {message && <Notice kind="success">{message}</Notice>}
      <ErrorNotice error={error} />

      {student ? (
        <div className="assignment">
          <div className="assignment-main">
            <p className="student-name">{student.studentName}</p>
            <span className="student-ref">{student.studentRef}</span>

            <dl className="kv assignment-meta">
              <dt>Assignment status</dt>
              <dd><span className="badge ok dot">Assigned</span></dd>
              <dt>Assigned since</dt>
              <dd>{formatDateTime(student.assignedAt)}</dd>
            </dl>
          </div>
        </div>
      ) : (
        <>
          <p className="muted" style={{ marginTop: 0 }}>
            No student is assigned to this device.
          </p>
          {/* Said plainly, because an administrator looking at a device enrolled
              before this existed should not have to wonder whether something is
              broken. */}
          <p className="small muted" style={{ marginBottom: 0 }}>
            Devices enrolled before student assignment was introduced have none. Assign one
            here, or it is recorded automatically the next time the device is enrolled with a
            student name.
          </p>
        </>
      )}

      {confirming && student && (
        <ConfirmDialog
          title="Release device assignment?"
          intro="This removes the current student assignment and allows the device to be reassigned through the approved enrollment workflow. It does not wipe or unenroll the device."
          confirmLabel="Release assignment"
          intent="danger"
          busy={busy}
          onCancel={() => setConfirming(false)}
          onConfirm={() => void release()}
        >
          <dl className="kv">
            <dt>Student</dt><dd><strong>{student.studentName}</strong></dd>
            <dt>Student ID</dt><dd className="mono">{student.studentRef}</dd>
            <dt>Device</dt><dd>{deviceLabel}</dd>
          </dl>
          <label style={{ marginTop: 14 }}>
            <span>Reason (optional)</span>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="End of term, device returned…"
            />
            <span className="field-hint">Recorded in the audit trail alongside the release.</span>
          </label>
        </ConfirmDialog>
      )}

      {assigning && (
        <ConfirmDialog
          title="Assign a student"
          intro="The Student ID is generated automatically. Enter the student's name only."
          confirmLabel="Assign student"
          busy={busy}
          onCancel={() => setAssigning(false)}
          onConfirm={() => void assign()}
        >
          <label>
            <span>Student name</span>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Rahul Kumar"
              autoFocus
            />
            <span className="field-hint">
              An existing student with this name keeps their Student ID; a new one is created
              otherwise.
            </span>
          </label>
          <label>
            <span>Reason (optional)</span>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Replacement device…"
            />
          </label>
        </ConfirmDialog>
      )}
    </Panel>
  );
}
